import { useSaveProcess } from "@/pages/caster/caster-data-entry-v2/hooks/useSaveProcess";
import { ButtonWithLoading, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	FormControl,
	Grid,
	InputLabel,
	TextField,
} from "@material-ui/core";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { LengthInput } from "../../../modals/delivery-ticket/ProductBatch/components/LengthInput";
import { ScrapCodeSelector } from "../components/ScrapCodeSelector";
import { IScrapRow } from "../hooks/useScrapTableData";
interface EditScrapModalProps {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	scrap: IScrapRow | null;
}

type FormValues = {
	totalLengthInInches: number;
	pieces: number | null;
	codeId: number | null;
};

export const EditScrapModal = ({
	show,
	onHide,
	scrap,
}: EditScrapModalProps) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const { saveProcessData } = useSaveProcess();
	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: {
			totalLengthInInches: 0,
			pieces: null,
			codeId: null,
		},
	});

	useEffect(() => {
		if (show && scrap) {
			reset({
				totalLengthInInches: scrap.length || 0,
				pieces: scrap.pieces || null,
				codeId: scrap.codeId || null,
			});
		} else if (!show) {
			reset({
				totalLengthInInches: 0,
				pieces: null,
				codeId: null,
			});
		}
	}, [show, scrap, reset]);

	const onSubmit: SubmitHandler<FormValues> = async (data) => {
		if (!scrap) return;
		setIsSubmitLoading(true);
		const lengthField = scrap.id === "scrap1" ? "Scrap1Length" : "Scrap2Length";
		const piecesField = scrap.id === "scrap1" ? "Scrap1Pieces" : "Scrap2Pieces";
		const codeField = scrap.id === "scrap1" ? "Scrap1CodeId" : "Scrap2CodeId";

		await saveProcessData(lengthField, data.totalLengthInInches || 0);
		await saveProcessData(piecesField, data.pieces || null);
		await saveProcessData(codeField, data.codeId);

		onHide(true);
		setIsSubmitLoading(false);
	};

	return (
		<MesfModal
			open={show}
			handleClose={() => onHide(false)}
			aria-labelledby="edit-scrap-modal-title"
			maxWidth="sm"
			title={`Edit Scrap ${scrap?.id ? scrap.id[scrap.id.length - 1] : ""}`}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
					<Grid container spacing={2} alignItems="flex-start">
						<Grid item xs={12} md={4}>
							<Controller
								name="totalLengthInInches"
								control={control}
								rules={{ min: 0 }}
								render={({ field }) => {
									const feet = Math.floor((field.value || 0) / 12);
									const inches = (field.value || 0) % 12;
									return (
										<LengthInput
											feet={feet}
											inches={inches}
											onChange={(newFeet: number, newInches: number) =>
												field.onChange(newFeet * 12 + newInches)
											}
											error={!!errors.totalLengthInInches}
											helperText={errors.totalLengthInInches?.message}
										/>
									);
								}}
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<Controller
								name="pieces"
								control={control}
								rules={{ min: 0 }}
								render={({ field }) => (
									<TextField
										{...field}
										label="Pieces"
										type="number"
										size="small"
										variant="outlined"
										fullWidth
										InputProps={{ inputProps: { min: 0 } }}
										error={!!errors.pieces}
										helperText={errors.pieces?.message}
										onChange={(e) =>
											field.onChange(parseInt(e.target.value) || null)
										}
										value={field.value === null ? "" : field.value}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12} md={4}>
							<FormControl
								variant="outlined"
								fullWidth
								size="small"
								error={!!errors.codeId}
							>
								<InputLabel id="scrap-code-label">Code</InputLabel>
								<Controller
									name="codeId"
									control={control}
									defaultValue={null}
									render={({ field }) => (
										<ScrapCodeSelector
											value={field.value}
											onChange={(value) => field.onChange(value)}
										/>
									)}
								/>
							</FormControl>
						</Grid>
					</Grid>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Grid container spacing={2} justifyContent="flex-end">
						<Grid item xs={12} md={3}>
							<Button
								fullWidth
								variant="contained"
								color="default"
								onClick={() => onHide(false)}
							>
								Cancel
							</Button>
						</Grid>
						<Grid item xs={12} md={3}>
							<ButtonWithLoading
								fullWidth
								isLoading={isSubmitLoading}
								variant="contained"
								color="primary"
								type="submit"
								disabled={!scrap}
							>
								Save
							</ButtonWithLoading>
						</Grid>
					</Grid>
				</MesfModal.Actions>
			</form>
		</MesfModal>
	);
};
